import { yup } from 'common/validation/initYup';

interface IMachineTransition<E, S> {
  from: S;
  to: S;
}

type StatePermissions<S extends string, A extends string> = {
  [SKey in S]: A[];
};

/**
 * Crea una macchina a stati finiti a partire da un'entità.
 * E' possibile specificare:
 * - Gli stati della FSM
 * - Le transizioni disponibili
 * - Le azioni disponibili in base allo stato
 * Nonché una funzione dedicata per prendere lo stato dall'entità.
 */
export function createMachine<
  E extends object,
  S extends string,
  A extends string
>(options: {
  /** Stati della FSM */
  states: S[];
  /** Azioni disponibili sull'entità */
  actions?: A[];
  /** Azioni disponibili in base allo stato corrente dell'entità */
  permissions?: StatePermissions<S, A>;
  /** Ritorna lo stato dell'entità a partire dall'entità stessa */
  getState: (entity: E) => S;

  transitions: IMachineTransition<E, S>[];
}) {
  const { states, getState, transitions } = options;
  const actions = options.actions ?? [];
  const permissions = options.permissions ?? ({} as StatePermissions<S, A>);

  return {
    /**
     * Controlla se l'entità è nello stato specificato
     */
    is(entity: Maybe<E>, state: S) {
      if (!entity) return false;
      return getState(entity) === state;
    },
    /**
     * Controlla se l'entità può transitare nello stato richiesto
     */
    to(entity: Maybe<E>, to: S) {
      if (!entity) return false;
      const from = getState(entity);
      return transitions.some(t => t.from === from && t.to === to);
    },
    /**
     * Controlla se l'entità è in uno stato che permette l'azione indicata
     */
    allows(entity: Maybe<E>, action: A) {
      if (!entity) return false;
      const permission = permissions[getState(entity)];
      return permission.some(p => p === action);
    }
  };
}
